import { defineMessages } from 'react-intl';

export const scope = 'app.containers.ScoreCardTemplates';

export default defineMessages({
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Scorecards',
    description: 'Title of the Scorecards company setting',
  },
  pageSubtitle: {
    id: `${scope}.pageSubtitle`,
    defaultMessage:
      'Build structured evaluation templates for interviews. Assign scorecards to stages, track completion, and{br}compare assessments side by side.',
    description: 'Explains what the Scorecards setting is for',
  },
  backToCompanySettings: {
    id: `${scope}.backToCompanySettings`,
    defaultMessage: 'Company settings',
    description:
      'Back link destination — the settings area this page belongs to',
  },
  createScorecard: {
    id: `${scope}.createScorecard`,
    defaultMessage: 'Create scorecard',
    description:
      'Button that opens the scorecard builder to create a new scorecard',
  },
  searchPlaceholder: {
    id: `${scope}.searchPlaceholder`,
    defaultMessage: 'Search scorecards',
  },
  resultsCount: {
    id: `${scope}.resultsCount`,
    defaultMessage: '{count, plural, one {# scorecard} other {# scorecards}}',
  },
  paginationPrevious: {
    id: `${scope}.paginationPrevious`,
    defaultMessage: 'Previous',
  },
  columnName: { id: `${scope}.columnName`, defaultMessage: 'Template name' },
  columnQuestions: {
    id: `${scope}.columnQuestions`,
    defaultMessage: 'Questions',
  },
  columnType: { id: `${scope}.columnType`, defaultMessage: 'Type' },
  columnStages: { id: `${scope}.columnStages`, defaultMessage: 'Stages' },
  columnLastUpdated: {
    id: `${scope}.columnLastUpdated`,
    defaultMessage: 'Last updated',
  },
  updatedByName: { id: `${scope}.updatedByName`, defaultMessage: 'by {name}' },
  // Scoring type labels. The backend enum is unweighted/weighted/text; the
  // product names for those modes are Scored/Weighted/Comments only.
  typeScored: { id: `${scope}.typeScored`, defaultMessage: 'Scored' },
  typeWeighted: { id: `${scope}.typeWeighted`, defaultMessage: 'Weighted' },
  typeCommentsOnly: {
    id: `${scope}.typeCommentsOnly`,
    defaultMessage: 'Comments only',
  },
  stagesCount: {
    id: `${scope}.stagesCount`,
    defaultMessage: '{count, plural, one {# stage} other {# stages}}',
  },
  attachedTo: {
    id: `${scope}.attachedTo`,
    defaultMessage: 'Attached to',
    description:
      'Header of the panel listing the stages a scorecard is attached to',
  },
  notInUse: {
    id: `${scope}.notInUse`,
    defaultMessage: 'Not in use',
    description: 'Stages cell for a scorecard not assigned to any stage yet',
  },
  duplicate: { id: `${scope}.duplicate`, defaultMessage: 'Duplicate' },
  deleteAction: { id: `${scope}.deleteAction`, defaultMessage: 'Delete' },
  inUseTitle: {
    id: `${scope}.inUseTitle`,
    defaultMessage: 'Scorecard in use',
    description: 'Header of the modal shown when a scorecard cannot be deleted',
  },
  inUseBody: {
    id: `${scope}.inUseBody`,
    defaultMessage:
      '{name} is attached to {count, plural, one {# workflow stage} other {# workflow stages}}. Remove it from {count, plural, one {that stage} other {those stages}} to delete it.',
    description: 'Explains why the scorecard cannot be deleted yet',
  },
  inUseClose: { id: `${scope}.inUseClose`, defaultMessage: 'Close' },
  deleteConfirmTitle: {
    id: `${scope}.deleteConfirmTitle`,
    defaultMessage: 'Delete scorecard?',
  },
  deleteConfirmBody: {
    id: `${scope}.deleteConfirmBody`,
    defaultMessage: "Delete {name}? This can't be undone.",
  },
  deleteConfirmButton: {
    id: `${scope}.deleteConfirmButton`,
    defaultMessage: 'Delete scorecard',
  },
  templateDuplicated: {
    id: `${scope}.templateDuplicated`,
    defaultMessage: 'Scorecard duplicated.',
  },
  templateDeleted: {
    id: `${scope}.templateDeleted`,
    defaultMessage: 'Scorecard deleted.',
  },
  deleteError: {
    id: `${scope}.deleteError`,
    defaultMessage: "We couldn't delete this scorecard. Please try again.",
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage:
      'We were unable to load scorecards. Refresh the page or try again later.',
  },
  noSearchResults: {
    id: `${scope}.noSearchResults`,
    defaultMessage: 'No scorecards match your search.',
  },
  emptyTitle: {
    id: `${scope}.emptyTitle`,
    defaultMessage: 'No scorecards yet',
  },
  emptyBody: {
    id: `${scope}.emptyBody`,
    defaultMessage:
      'Create your first scorecard to evaluate applicants during interviews.',
  },
  panelClose: { id: `${scope}.panelClose`, defaultMessage: 'Close panel' },
  newTemplateTitle: {
    id: `${scope}.newTemplateTitle`,
    defaultMessage: 'New scorecard',
  },
  templateNameLabel: {
    id: `${scope}.templateNameLabel`,
    defaultMessage: 'Scorecard name',
  },
  templateNamePlaceholder: {
    id: `${scope}.templateNamePlaceholder`,
    defaultMessage: 'Name this scorecard',
  },
  templateNameRequired: {
    id: `${scope}.templateNameRequired`,
    defaultMessage: 'Enter a scorecard name',
  },
  scoringModeLabel: {
    id: `${scope}.scoringModeLabel`,
    defaultMessage: 'Scoring',
  },
  scoredHint: {
    id: `${scope}.scoredHint`,
    defaultMessage: 'Entries are rated and averaged into a single score.',
  },
  weightedHint: {
    id: `${scope}.weightedHint`,
    defaultMessage:
      'Each entry carries a weight; scores combine into a weighted total.',
  },
  commentsOnlyHint: {
    id: `${scope}.commentsOnlyHint`,
    defaultMessage:
      'Collects written feedback and a recommendation. No score is calculated.',
  },
  scoredQuestionsNote: {
    id: `${scope}.scoredQuestionsNote`,
    defaultMessage: 'Only rating scale questions count toward the score.',
    description:
      'Shown on a scored or weighted scorecard, above the question list',
  },
  weightAllocated: {
    id: `${scope}.weightAllocated`,
    defaultMessage: 'Weight allocated',
  },
  weightAllocatedValue: {
    id: `${scope}.weightAllocatedValue`,
    defaultMessage: '{total}% of 100%',
  },
  weightsComplete: {
    id: `${scope}.weightsComplete`,
    defaultMessage: 'Weights add up — this scorecard is ready to save.',
  },
  weightsOver: {
    id: `${scope}.weightsOver`,
    defaultMessage:
      'Question weights total {total}% — remove {over}% before saving.',
  },
  weightsUnder: {
    id: `${scope}.weightsUnder`,
    defaultMessage:
      '{remaining}% still unassigned. Weights must total 100% before you can save.',
  },
  weightsScaleNote: {
    id: `${scope}.weightsScaleNote`,
    defaultMessage:
      'Scores stay on the 1–5 scale. Each percentage is converted to a point weight of 1–5 — the heaviest question becomes 5 and the rest scale against it.',
    description:
      'Explains how the percentages a recruiter types relate to the score a reviewer produces',
  },
  weightLabel: { id: `${scope}.weightLabel`, defaultMessage: 'Weight' },
  weightFieldLabel: {
    id: `${scope}.weightFieldLabel`,
    defaultMessage: 'Weight for {title}, percent',
  },
  entryTitlePlaceholder: {
    id: `${scope}.entryTitlePlaceholder`,
    defaultMessage: 'Question title',
  },
  entryTitleLabel: {
    id: `${scope}.entryTitleLabel`,
    defaultMessage: 'Title for question {number}',
  },
  entryTitleRequired: {
    id: `${scope}.entryTitleRequired`,
    defaultMessage: 'Enter a title for this question',
  },
  entryDescriptionPlaceholder: {
    id: `${scope}.entryDescriptionPlaceholder`,
    defaultMessage: 'Add a description (optional)',
  },
  entryDescriptionLabel: {
    id: `${scope}.entryDescriptionLabel`,
    defaultMessage: 'Description for question {number}',
  },
  entryTypeLabelFor: {
    id: `${scope}.entryTypeLabelFor`,
    defaultMessage: 'Type for question {number}',
  },
  typeRating: { id: `${scope}.typeRating`, defaultMessage: 'Rating scale' },
  typeYesNo: { id: `${scope}.typeYesNo`, defaultMessage: 'Yes / No' },
  typeDropdown: { id: `${scope}.typeDropdown`, defaultMessage: 'Dropdown' },
  typeFreeText: { id: `${scope}.typeFreeText`, defaultMessage: 'Free text' },
  typeRecommendation: {
    id: `${scope}.typeRecommendation`,
    defaultMessage: 'Recommendation',
  },
  optionPlaceholder: {
    id: `${scope}.optionPlaceholder`,
    defaultMessage: 'Add a choice',
  },
  optionInputLabel: {
    id: `${scope}.optionInputLabel`,
    defaultMessage: 'New choice for {title}',
  },
  removeOption: {
    id: `${scope}.removeOption`,
    defaultMessage: 'Remove {label}',
  },
  optionsRequired: {
    id: `${scope}.optionsRequired`,
    defaultMessage: 'Add at least one choice',
  },
  hintTextPlaceholder: {
    id: `${scope}.hintTextPlaceholder`,
    defaultMessage: 'Hint text for interviewer (optional)',
  },
  hintTextLabel: {
    id: `${scope}.hintTextLabel`,
    defaultMessage: 'Interviewer hint for {title}',
  },
  allowNotes: { id: `${scope}.allowNotes`, defaultMessage: 'Allow notes' },
  duplicateEntry: {
    id: `${scope}.duplicateEntry`,
    defaultMessage: 'Duplicate question',
  },
  deleteEntry: {
    id: `${scope}.deleteEntry`,
    defaultMessage: 'Delete question',
  },
  deleteEntryConfirmTitle: {
    id: `${scope}.deleteEntryConfirmTitle`,
    defaultMessage: 'Delete this question?',
    description:
      'Title for the confirmation shown before deleting a saved scorecard question.',
  },
  deleteEntryConfirmBody: {
    id: `${scope}.deleteEntryConfirmBody`,
    defaultMessage:
      'Evaluations still in progress lose their answers to this question when the scorecard is saved.',
    description:
      'Confirmation shown before deleting a saved question. Deliberately promises nothing about submitted answers: they survive in the database and the PDF through their question snapshot, but the results modal renders the live template and drops them. Extend this once the snapshot reaches that modal.',
  },
  reorderEntry: {
    id: `${scope}.reorderEntry`,
    defaultMessage: 'Reorder question {number}',
  },
  addEntry: { id: `${scope}.addEntry`, defaultMessage: 'Add question' },
  recommendationTitle: {
    id: `${scope}.recommendationTitle`,
    defaultMessage: 'Overall recommendation',
  },
  recommendationNoteRequired: {
    id: `${scope}.recommendationNoteRequired`,
    defaultMessage: 'Every evaluator selects one before submitting.',
  },
  recommendationNoteOptional: {
    id: `${scope}.recommendationNoteOptional`,
    defaultMessage: 'Optional — evaluators can submit without choosing one.',
  },
  saveTemplate: {
    id: `${scope}.saveTemplate`,
    defaultMessage: 'Save scorecard',
  },
  saveFailed: {
    id: `${scope}.saveFailed`,
    defaultMessage:
      "We couldn't save this scorecard. Check your connection and try again.",
  },
  templateSaved: {
    id: `${scope}.templateSaved`,
    defaultMessage: 'Scorecard saved.',
  },
  templateCreated: {
    id: `${scope}.templateCreated`,
    defaultMessage: 'Scorecard created.',
  },
  panelLoadError: {
    id: `${scope}.panelLoadError`,
    defaultMessage:
      "We couldn't load this scorecard. Close the panel and open it again.",
  },
  saveBlockedWeights: {
    id: `${scope}.saveBlockedWeights`,
    defaultMessage:
      'Question weights must total 100%. They currently total {total}%.',
  },
  fixErrorsBeforeSaving: {
    id: `${scope}.fixErrorsBeforeSaving`,
    defaultMessage: 'Fix the highlighted entries before saving.',
  },
  discardConfirmTitle: {
    id: `${scope}.discardConfirmTitle`,
    defaultMessage: 'Discard unsaved changes?',
  },
  discardConfirmBody: {
    id: `${scope}.discardConfirmBody`,
    defaultMessage: "Your edits to this scorecard haven't been saved yet.",
  },
});
